import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Table,
  Badge,
  Button,
  Modal,
  Alert,
} from "react-bootstrap";
import { useWeb3Context } from "../context/Web3Context";
import { useWallet } from "@solana/wallet-adapter-react";
import { bridgeService } from "../lib/bridgeService";
import { toast } from "react-toastify";
import Header from "../components/Header";
import Footer from "../components/Footer";
import WalletConnect from "../components/WalletConnect";
import { Web3Button } from "../components/web3UI/Web3Button";
import "./BridgeHistoryStyles.scss";
import { Link } from "react-router-dom";

const BridgeHistory = () => {
  const [transactions, setTransactions] = useState([]);
  console.log(
    "🚀 ~ BridgeHistory.jsx:15 ~ BridgeHistory ~ transactions:",
    transactions
  );

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedTx, setSelectedTx] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [stats, setStats] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [showWalletSelection, setShowWalletSelection] = useState(false);

  // BSC Web3 Context
  const { address: bscAddress } = useWeb3Context();

  // Solana Wallet Context
  const { publicKey: solanaPublicKey, connected: solanaConnected } =
    useWallet();

  useEffect(() => {
    const loadData = async () => {
      await loadBridgeHistory();
      await loadBridgeStats();
      setLastRefresh(new Date());
    };

    loadData();
  }, [bscAddress, solanaPublicKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Add keyboard shortcut for refresh (Ctrl+R or Cmd+R)
  useEffect(() => {
    const handleKeyPress = (event) => {
      if ((event.ctrlKey || event.metaKey) && event.key === "r") {
        event.preventDefault();
        if (!refreshing && !loading) {
          refreshData();
        }
      }
    };

    document.addEventListener("keydown", handleKeyPress);
    return () => {
      document.removeEventListener("keydown", handleKeyPress);
    };
  }, [refreshing, loading]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadBridgeHistory = async () => {
    try {
      setLoading(true);
      console.log("Loading bridge history for addresses:", {
        bscAddress,
        solanaAddress: solanaPublicKey?.toString(),
      });

      // Get transactions for both connected addresses
      let allTransactions = [];

      if (bscAddress) {
        console.log("Fetching BSC bridge requests...");
        const bscTxs = await bridgeService.getUserBridgeRequests(bscAddress);
        console.log("Fetched BSC transactions:", bscTxs.length);
        allTransactions = [...allTransactions, ...bscTxs];
      }

      if (solanaPublicKey) {
        console.log("Fetching Solana bridge requests...");
        const solanaTxs = await bridgeService.getUserBridgeRequests(
          solanaPublicKey.toString()
        );
        console.log("Fetched Solana transactions:", solanaTxs.length);
        allTransactions = [...allTransactions, ...solanaTxs];
      }

      if (!bscAddress && !solanaPublicKey) {
        console.log("No wallets connected, skipping transaction fetch");
        allTransactions = [];
      }

      // Remove duplicates and sort by creation date (newest first)
      const uniqueTransactions = allTransactions.filter(
        (tx, index, self) => index === self.findIndex((t) => t.id === tx.id)
      );
      uniqueTransactions.sort(
        (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
      );

      console.log("Setting unique transactions:", uniqueTransactions.length);
      setTransactions(uniqueTransactions);
    } catch (error) {
      console.error("Error loading bridge history:", error);
      toast.error("Failed to load bridge history");
    } finally {
      setLoading(false);
    }
  };

  const refreshData = async () => {
    try {
      setRefreshing(true);
      console.log("Refreshing bridge history data...");

      // Force a fresh fetch by clearing current data first
      setTransactions([]);
      setStats(null);

      await loadBridgeHistory();
      await loadBridgeStats();

      setLastRefresh(new Date());
      toast.success("Bridge history refreshed successfully!");
      console.log("Refresh completed successfully");
    } catch (error) {
      console.error("Error refreshing data:", error);
      toast.error("Failed to refresh data. Please try again.");
    } finally {
      setRefreshing(false);
    }
  };

  const loadBridgeStats = async () => {
    try {
      console.log("Loading bridge stats for addresses:", {
        bscAddress,
        solanaAddress: solanaPublicKey?.toString(),
      });

      // Calculate stats from combined transactions
      let allUserTxs = [];

      if (bscAddress) {
        const bscTxs = await bridgeService.getUserBridgeRequests(bscAddress);
        allUserTxs = [...allUserTxs, ...bscTxs];
      }

      if (solanaPublicKey) {
        const solanaTxs = await bridgeService.getUserBridgeRequests(
          solanaPublicKey.toString()
        );
        allUserTxs = [...allUserTxs, ...solanaTxs];
      }

      // Remove duplicates
      const uniqueTxs = allUserTxs.filter(
        (tx, index, self) => index === self.findIndex((t) => t.id === tx.id)
      );

      console.log(
        "Calculating stats from unique transactions:",
        uniqueTxs.length
      );

      if (uniqueTxs.length > 0) {
        const userStats = {
          total: uniqueTxs.length,
          pending: uniqueTxs.filter((tx) => tx.status === "pending").length,
          approved: uniqueTxs.filter((tx) => tx.status === "approved").length,
          completed: uniqueTxs.filter((tx) => tx.status === "completed").length,
          rejected: uniqueTxs.filter((tx) => tx.status === "rejected").length,
          totalVolume: uniqueTxs.reduce(
            (sum, tx) => sum + parseFloat(tx.amount || 0),
            0
          ),
          totalFees: uniqueTxs.reduce(
            (sum, tx) => sum + parseFloat(tx.bridgeFee || 0),
            0
          ),
        };

        console.log("Calculated stats:", userStats);
        setStats(userStats);
      } else {
        console.log("No transactions found, clearing stats");
        setStats(null);
      }
    } catch (error) {
      console.error("Error loading bridge stats:", error);
      toast.error("Failed to load bridge statistics");
    }
  };

  const handleViewDetails = (transaction) => {
    setSelectedTx(transaction);
    setShowModal(true);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "completed":
        return <Badge bg="success">Completed</Badge>;
      case "approved":
        return <Badge bg="info">Approved</Badge>;
      case "pending":
        return <Badge bg="warning">Pending Review</Badge>;
      case "rejected":
        return <Badge bg="danger">Rejected</Badge>;
      default:
        return <Badge bg="secondary">{status}</Badge>;
    }
  };

  const formatAddress = (address) => {
    if (!address) return "N/A";
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleString();
  };

  const openTxInExplorer = (txHash, chain) => {
    let url;
    if (chain === "bsc") {
      url = `https://bscscan.com/tx/${txHash}`;
    } else if (chain === "solana") {
      url = `https://solscan.io/tx/${txHash}`;
    }

    if (url) {
      window.open(url, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <div className="dashboard-body">
      <div className="bridge-history-page">
        <div className="dashboard-head">
          <div className="logo">
            <Link to="/">
              <img
                className="main_logo"
                src={require("../images/logo.png")}
                style={{ width: "200px" }}
              />
            </Link>
          </div>
          <div className="buttons">
            <Link to="/">
              <button className="btn main-btn">Home</button>
            </Link>
            <Web3Button />
          </div>
        </div>
        <Container className="bridge-history-container">
          <Row>
            <Col>
              <h2 className="page-title">Bridge Transaction History</h2>
              <p className="page-subtitle">
                Track your cross-chain DBN token transfers
              </p>

              {/* Connected Wallets Status */}
              <div className="connect-wallect-main-wrapper mb-4">
                <div className="main-wrapper">
                  <div>
                    <h6 className="mb-0">Connected Wallets</h6>
                    <small className="text-muted">
                      {lastRefresh &&
                        `Last updated: ${lastRefresh.toLocaleTimeString()}`}
                    </small>
                  </div>
                  <div className="btns-wrapper">
                    <div className="wallet-status">
                      <span
                        className="badge me-2"
                        style={{
                          backgroundColor: bscAddress ? "#28a745" : "#6c757d",
                        }}
                      >
                        <i className="fab fa-ethereum me-1"></i>
                        BSC {bscAddress ? "Connected" : "Disconnected"}
                      </span>
                      {bscAddress && (
                        <small className="text-muted d-block">
                          {bscAddress.slice(0, 6)}...{bscAddress.slice(-4)}
                        </small>
                      )}
                    </div>

                    <div className="wallet-status">
                      <span
                        className="badge me-2"
                        style={{
                          backgroundColor: solanaConnected
                            ? "#9945ff"
                            : "#6c757d",
                        }}
                      >
                        <i className="fas fa-sun me-1"></i>
                        Solana {solanaConnected ? "Connected" : "Disconnected"}
                      </span>
                      {/* {solanaPublicKey && (
                          <small className="text-muted d-block">
                            {solanaPublicKey.toString().slice(0, 8)}...
                          </small>
                        )} */}
                    </div>

                    {(!bscAddress || !solanaConnected) && (
                      <Button
                        className="main-btn"
                        size="sm"
                        onClick={() => setShowWalletSelection(true)}
                      >
                        Connect Wallet
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </Col>
          </Row>

          {/* Statistics Cards */}
          {stats && (
            <Row className="mb-4">
              <Col md={3}>
                <Card className="stat-card">
                  <Card.Body className="text-center">
                    <h3 className="stat-number">{stats.total}</h3>
                    <p className="stat-label">Total Requests</p>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3}>
                <Card className="stat-card">
                  <Card.Body className="text-center">
                    <h3 className="stat-number">{stats.pending}</h3>
                    <p className="stat-label">Pending</p>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3}>
                <Card className="stat-card">
                  <Card.Body className="text-center">
                    <h3 className="stat-number">{stats.completed}</h3>
                    <p className="stat-label">Completed</p>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3}>
                <Card className="stat-card">
                  <Card.Body className="text-center">
                    <h3 className="stat-number">
                      {stats.totalVolume.toFixed(2)}
                    </h3>
                    <p className="stat-label">Total Volume (DBN)</p>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          )}

          {/* Transactions Table */}
          <Row>
            <Col>
              <div className="transactions-card">
                <div className="header-wrapper ">
                  <div>
                    <h5 className="mb-0">Transaction History</h5>
                    {lastRefresh && (
                      <small className="text-muted">
                        Last updated: {lastRefresh.toLocaleTimeString()}
                      </small>
                    )}
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    <Button
                      size="sm"
                      onClick={refreshData}
                      disabled={refreshing || loading}
                      className="main-btn gap-2 d-inline-flex align-items-center"
                      title="Refresh data (Ctrl+R)"
                    >
                      <i
                        className={`fas fa-${
                          refreshing ? "spinner fa-spin" : "sync-alt"
                        }`}
                      ></i>
                      {refreshing ? "Refreshing..." : "Refresh"}
                    </Button>
                  </div>
                </div>
                <Card.Body>
                  {loading ? (
                    <div className="text-center py-4">
                      <div className="spinner-border" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </div>
                  ) : (
                    <>
                      {/* Debug info in development */}
                      {process.env.NODE_ENV === "development" && (
                        <div className="features-wrapper">
                          <strong>Debug Info:</strong>
                          <br />
                          <ul className="list">
                            <li>
                              BSC Address: {bscAddress || "Not connected"}
                            </li>
                            <li>
                              Solana PublicKey:{" "}
                              {solanaPublicKey?.toString() || "Not connected"}
                            </li>
                            <li>
                              Solana Connected: {solanaConnected ? "Yes" : "No"}
                            </li>
                            <li>Transactions Count: {transactions.length}</li>
                          </ul>
                        </div>
                      )}

                      {transactions.length === 0 ? (
                        !bscAddress && !solanaPublicKey ? (
                          // No wallets connected - show wallet connection options
                          <div className="connect-wallet-section">
                            <h4 className="mb-4">Connect Your Wallets</h4>
                            <p className="text-muted mb-4">
                              Connect your BSC and/or Solana wallets to view
                              your bridge transaction history
                            </p>
                            <div className="connect-wallet-card-wrapper">
                              <Row className="justify-content-center">
                                <Col md={5} className="mb-3">
                                  <Card className="wallet-option-card h-100">
                                    <Card.Body className="text-center">
                                      <div className="wallet-icon mb-3">
                                        <i className="fab fa-ethereum fa-3x text-warning"></i>
                                      </div>
                                      <h5>BSC Wallet</h5>
                                      <p className="text-muted small">
                                        Connect your BSC wallet to view BSC →
                                        Solana bridge transactions
                                      </p>
                                      <Web3Button />
                                    </Card.Body>
                                  </Card>
                                </Col>

                                <Col md={5} className="mb-3">
                                  <Card className="wallet-option-card h-100">
                                    <Card.Body className="text-center">
                                      <div className="wallet-icon mb-3">
                                        <i className="fas fa-sun fa-3x text-purple"></i>
                                      </div>
                                      <h5>Solana Wallet</h5>
                                      <p className="text-muted small">
                                        Connect your Solana wallet to view
                                        Solana → BSC bridge transactions
                                      </p>
                                      <WalletConnect />
                                    </Card.Body>
                                  </Card>
                                </Col>
                              </Row>
                            </div>

                            <Alert variant="info" className="mt-4">
                              <strong>Tip:</strong> You can connect both wallets
                              to view all your bridge transactions in one place
                            </Alert>
                          </div>
                        ) : (
                          // Wallets connected but no transactions
                          <div className="text-center py-4">
                            <i className="fas fa-exchange-alt fa-3x text-muted mb-3"></i>
                            <h5>No Bridge Transactions Found</h5>
                            <p className="text-muted">
                              {bscAddress && solanaPublicKey
                                ? "No transactions found for your connected wallets"
                                : `No transactions found for your ${
                                    bscAddress ? "BSC" : "Solana"
                                  } wallet`}
                            </p>

                            {(!bscAddress || !solanaPublicKey) && (
                              <div className="mt-3">
                                <p className="text-muted small mb-3">
                                  Connect your other wallet to see more
                                  transactions:
                                </p>
                                <div className="d-flex justify-content-center gap-3">
                                  {!bscAddress && (
                                    <div>
                                      <p className="small mb-2">BSC Wallet:</p>
                                      <Web3Button />
                                    </div>
                                  )}
                                  {!solanaPublicKey && (
                                    <div>
                                      <p className="small mb-2">
                                        Solana Wallet:
                                      </p>
                                      <WalletConnect />
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        )
                      ) : (
                        <div className="table-responsive pt-0">
                          <Table>
                            <thead>
                              <tr>
                                <th>Date</th>
                                <th>Direction</th>
                                <th>From Address</th>
                                <th>To Address</th>
                                <th>Amount</th>
                                <th>Fee</th>
                                <th>Status</th>
                                <th>Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {transactions.map((tx, index) => (
                                <tr key={index}>
                                  <td>{formatDate(tx.createdAt)}</td>
                                  <td>
                                    <Badge
                                      bg={
                                        tx.direction === "bsc-to-solana"
                                          ? "warning"
                                          : "info"
                                      }
                                    >
                                      {tx.direction === "bsc-to-solana"
                                        ? "BSC → Solana"
                                        : "Solana → BSC"}
                                    </Badge>
                                  </td>
                                  <td>
                                    <code>
                                      {tx.direction === "bsc-to-solana"
                                        ? formatAddress(tx.fromAddress)
                                        : formatAddress(tx.fromSolanaAddress)}
                                    </code>
                                  </td>
                                  <td>
                                    <code>
                                      {tx.direction === "bsc-to-solana"
                                        ? formatAddress(tx.toSolanaAddress)
                                        : formatAddress(tx.toBSCAddress)}
                                    </code>
                                  </td>
                                  <td>
                                    <strong>
                                      {parseFloat(tx.amount || 0).toFixed(4)}{" "}
                                      DBN
                                    </strong>
                                    {tx.netAmount && (
                                      <div className="text-muted small">
                                        Net:{" "}
                                        {parseFloat(tx.netAmount).toFixed(4)}{" "}
                                        DBN
                                      </div>
                                    )}
                                  </td>
                                  <td>
                                    {parseFloat(tx.bridgeFee || 0).toFixed(4)}{" "}
                                    DBN
                                  </td>
                                  <td>{getStatusBadge(tx.status)}</td>
                                  <td>
                                    <Button
                                      className="main-btn"
                                      size="sm"
                                      onClick={() => handleViewDetails(tx)}
                                    >
                                      Details
                                    </Button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </div>
                      )}
                    </>
                  )}
                </Card.Body>
              </div>
            </Col>
          </Row>
        </Container>

        {/* Transaction Details Modal */}
        <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>Transaction Details</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {selectedTx && (
              <div className="transaction-details">
                <div className="transaction-detail-row">
                  <div className="transaction-detail-label">
                    <strong>Status:</strong>
                  </div>
                  <div className="transaction-detail-value">
                    {getStatusBadge(selectedTx.status)}
                  </div>
                </div>
                <div className="transaction-detail-row">
                  <div className="transaction-detail-label">
                    <strong>Direction:</strong>
                  </div>
                  <div className="transaction-detail-value">
                    <Badge
                      bg={
                        selectedTx.direction === "bsc-to-solana"
                          ? "warning"
                          : "info"
                      }
                    >
                      {selectedTx.direction === "bsc-to-solana"
                        ? "BSC → Solana"
                        : "Solana → BSC"}
                    </Badge>
                  </div>
                </div>
                <div className="transaction-detail-row">
                  <div className="transaction-detail-label">
                    <strong>Date:</strong>
                  </div>
                  <div className="transaction-detail-value">
                    {formatDate(selectedTx.createdAt)}
                  </div>
                </div>
                <div className="transaction-detail-row">
                  <div className="transaction-detail-label">
                    <strong>Amount:</strong>
                  </div>
                  <div className="transaction-detail-value">
                    {parseFloat(selectedTx.amount || 0).toFixed(4)} DBN
                  </div>
                </div>
                <div className="transaction-detail-row">
                  <div className="transaction-detail-label">
                    <strong>Net Amount:</strong>
                  </div>
                  <div className="transaction-detail-value">
                    {parseFloat(selectedTx.netAmount || 0).toFixed(4)} DBN
                  </div>
                </div>
                <div className="transaction-detail-row">
                  <div className="transaction-detail-label">
                    <strong>Bridge Fee:</strong>
                  </div>
                  <div className="transaction-detail-value">
                    {parseFloat(selectedTx.bridgeFee || 0).toFixed(4)} DBN
                  </div>
                </div>
                <div className="transaction-detail-row">
                  <div className="transaction-detail-label">
                    <strong>From Address:</strong>
                  </div>
                  <div className="transaction-detail-value">
                    <code>
                      {selectedTx.direction === "bsc-to-solana"
                        ? selectedTx.fromAddress
                        : selectedTx.fromSolanaAddress}
                    </code>
                  </div>
                </div>
                <div className="transaction-detail-row">
                  <div className="transaction-detail-label">
                    <strong>To Address:</strong>
                  </div>
                  <div className="transaction-detail-value">
                    <code>
                      {selectedTx.direction === "bsc-to-solana"
                        ? selectedTx.toSolanaAddress
                        : selectedTx.toBSCAddress}
                    </code>
                  </div>
                </div>
                {selectedTx.bscTxHash && (
                  <div className="transaction-detail-row">
                    <div className="transaction-detail-label">
                      <strong>BSC Transaction:</strong>
                    </div>
                    <div className="transaction-detail-value">
                      <Button
                        variant="link"
                        size="sm"
                        onClick={() =>
                          openTxInExplorer(selectedTx.bscTxHash, "bsc")
                        }
                        className="p-0"
                      >
                        {formatAddress(selectedTx.bscTxHash)} ↗
                      </Button>
                    </div>
                  </div>
                )}
                {selectedTx.solanaSignature && (
                  <div className="transaction-detail-row">
                    <div className="transaction-detail-label">
                      <strong>Solana Transaction:</strong>
                    </div>
                    <div className="transaction-detail-value">
                      <Button
                        variant="link"
                        size="sm"
                        onClick={() =>
                          openTxInExplorer(selectedTx.solanaSignature, "solana")
                        }
                        className="p-0"
                      >
                        {formatAddress(selectedTx.solanaSignature)} ↗
                      </Button>
                    </div>
                  </div>
                )}
                {selectedTx.adminNotes && (
                  <div className="transaction-detail-row">
                    <div className="transaction-detail-label">
                      <strong>Admin Notes:</strong>
                    </div>
                    <div className="transaction-detail-value">
                      <div className="text-info">{selectedTx.adminNotes}</div>
                    </div>
                  </div>
                )}
                {selectedTx.error && (
                  <div className="transaction-detail-row">
                    <div className="transaction-detail-label">
                      <strong>Error:</strong>
                    </div>
                    <div className="transaction-detail-value">
                      <div className="text-danger">{selectedTx.error}</div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              Close
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Wallet Selection Modal */}
        <Modal
          show={showWalletSelection}
          onHide={() => setShowWalletSelection(false)}
          size="lg"
          aria-labelledby="contained-modal-title-vcenter"
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title id="contained-modal-title-vcenter">
              Connect Wallet
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="text-center">
              <p className="mb-4">Choose which wallet you'd like to connect:</p>

              <Row>
                <Col md={6} className="mb-3">
                  <Card
                    className={`wallet-selection-card ${
                      bscAddress ? "connected" : ""
                    }`}
                  >
                    <Card.Body className="text-center p-4">
                      <div className="wallet-icon mb-3">
                        <i className="fab fa-ethereum fa-2x text-warning"></i>
                      </div>
                      <h6>BSC Wallet</h6>
                      <p className="text-muted small mb-3">
                        MetaMask, Trust Wallet, etc.
                      </p>
                      {bscAddress ? (
                        <div>
                          <Badge bg="success" className="mb-2">
                            Connected
                          </Badge>
                          <div className="small text-muted">
                            {bscAddress.slice(0, 6)}...{bscAddress.slice(-4)}
                          </div>
                        </div>
                      ) : (
                        <Web3Button />
                      )}
                    </Card.Body>
                  </Card>
                </Col>

                <Col md={6} className="mb-3">
                  <Card
                    className={`wallet-selection-card ${
                      solanaConnected ? "connected" : ""
                    }`}
                  >
                    <Card.Body className="text-center p-4">
                      <div className="wallet-icon mb-3">
                        <i className="fas fa-sun fa-2x text-purple"></i>
                      </div>
                      <h6>Solana Wallet</h6>
                      <p className="text-muted small mb-3">
                        Phantom, Solflare, etc.
                      </p>
                      {solanaConnected ? (
                        <div>
                          <Badge bg="success" className="mb-2">
                            Connected
                          </Badge>
                          <div className="small text-muted">
                            {solanaPublicKey?.toString().slice(0, 8)}...
                          </div>
                        </div>
                      ) : (
                        <WalletConnect />
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              <Alert variant="info" className="mt-3">
                <small>
                  <strong>Tip:</strong> Connect both wallets to view all your
                  bridge transactions
                </small>
              </Alert>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => setShowWalletSelection(false)}
            >
              Done
            </Button>
          </Modal.Footer>
        </Modal>
        <div className="footer_section">
          <div className="section_container">
            <img
              src={require("../images/logo.png")}
              style={{ width: "250px" }}
            />
            <p>
              We make it easy to Discover, Invest and manage all your NFTs at
              one place.
            </p>
            <div className="icons">
              <a href="https://twitter.com/DashBone1" target="_blank">
                <i className="fa-brands fa-twitter"></i>
              </a>
              <a href="https://discord.gg/KVpw6gjzPf" target="_blank">
                <i className="fa-brands fa-discord"></i>
              </a>
              <a
                href="https://www.instagram.com/dashboneofficial/"
                target="_blank"
              >
                <i className="fa-brands fa-instagram"></i>
              </a>

              <a
                href="https://www.linkedin.com/in/dash-bone-11b37b268/"
                target="_blank"
              >
                <i className="fa-brands fa-linkedin-in"></i>
              </a>
              <a href="https://medium.com/@dashbone" target="_blank">
                <i className="fa-brands fa-medium"></i>
              </a>
              <a
                href="https://www.pinterest.com.au/dashboneio/"
                target="_blank"
              >
                <i className="fa-brands fa-pinterest-p"></i>
              </a>
              <a
                href="https://www.quora.com/profile/Dash-Bone-2"
                target="_blank"
              >
                <i className="fa-brands fa-quora"></i>
              </a>
            </div>
            <img
              className="anim_pic14"
              src={require("../images/circle-01.png")}
              style={{ width: "16px" }}
            />
            <img
              className="anim_pic15"
              src={require("../images/ethereum-01.png")}
              style={{ width: "32px" }}
            />
            <img
              className="anim_pic16"
              src={require("../images/x-01.png")}
              style={{ width: "16px" }}
            />
          </div>
        </div>
        <div className="footer_bottom">
          <div className="section_container">
            <span>© 2023 Dashbone. All rights reserved.</span>
            {/* <span>© 2022 Dashbone. All rights reserved.</span> */}
          </div>
          <img
            className="anim_pic17"
            src={require("../images/circle-03.png")}
            style={{ width: "24px" }}
          />
        </div>
      </div>
    </div>
  );
};

export default BridgeHistory;
